import { MOTION, RADIUS } from "@elyxeer/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import { INTL_LOCALE, type Locale } from "~/i18n";
import { buildMonthGrid, dayKey, weekdayLabels } from "~/lib/calendar";

/**
 * The month grid.
 *
 * Presentational: it is told which month to draw, which days have events and which day
 * is selected, and reports clicks back. All the date arithmetic lives in `lib/calendar`,
 * where it is unit-tested without a DOM.
 *
 * Every cell is a real `<button>` so the grid is reachable by keyboard and each day
 * announces its full date rather than a bare number.
 */
export function EventCalendar({
  month,
  locale,
  selected,
  eventDays,
  onSelect,
  onMonthChange,
}: {
  /** Any date inside the month to render. */
  month: Date;
  locale: Locale;
  /** `null` when no day is picked — the list then covers the whole month. */
  selected: Date | null;
  /** Local day keys (`YYYY-MM-DD`) that have at least one event. */
  eventDays: Set<string>;
  onSelect: (day: Date) => void;
  onMonthChange: (month: Date) => void;
}) {
  const { t } = useTranslation();
  const intlLocale = INTL_LOCALE[locale];

  const weeks = buildMonthGrid(month);
  const weekdays = weekdayLabels(intlLocale);
  const selectedKey = selected ? dayKey(selected) : null;

  const monthLabel = new Intl.DateTimeFormat(intlLocale, {
    month: "long",
    year: "numeric",
  }).format(month);
  const fullDate = new Intl.DateTimeFormat(intlLocale, { dateStyle: "full" });

  return (
    <Paper variant="outlined" sx={{ padding: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <IconButton
          onClick={() =>
            onMonthChange(
              new Date(month.getFullYear(), month.getMonth() - 1, 1),
            )
          }
          aria-label={t("events.previousMonth")}
          data-testid="events.prev-month"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          variant="L1"
          sx={{ color: "text.primary", textTransform: "capitalize" }}
          data-testid="events.month"
        >
          {monthLabel}
        </Typography>

        <IconButton
          onClick={() =>
            onMonthChange(
              new Date(month.getFullYear(), month.getMonth() + 1, 1),
            )
          }
          aria-label={t("events.nextMonth")}
          data-testid="events.next-month"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
        }}
      >
        {weekdays.map((label) => (
          <Typography
            key={label}
            variant="L3"
            aria-hidden="true"
            sx={{
              color: "text.muted",
              textAlign: "center",
              paddingBlock: 1,
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        ))}

        {weeks.flat().map((day) => {
          const isSelected = day.key === selectedKey;
          const hasEvents = eventDays.has(day.key);

          return (
            <Box
              key={day.key}
              component="button"
              type="button"
              onClick={() => onSelect(day.date)}
              aria-label={fullDate.format(day.date)}
              aria-current={day.isToday ? "date" : undefined}
              aria-pressed={isSelected}
              data-testid={`events.day-${day.key}`}
              data-has-events={hasEvents}
              sx={{
                // Square cells, so the grid keeps its shape at any width — up to a cap.
                // A tablet stacks the grid across the whole page, where uncapped cells
                // grew to 90px and the month alone filled the screen; past the cap the
                // columns keep spreading and the days stay put in their centres.
                aspectRatio: "1",
                width: "100%",
                maxWidth: 56,
                justifySelf: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                border: "1px solid transparent",
                borderRadius: `${RADIUS.sm}px`,
                cursor: "pointer",
                font: "inherit",
                transition: `background-color ${MOTION.transition}, border-color ${MOTION.transition}`,
                backgroundColor: isSelected ? "primary.main" : "transparent",
                // Today is outlined rather than filled, so it never competes with the
                // selection for the same visual slot.
                borderColor:
                  !isSelected && day.isToday ? "primary.main" : "transparent",
                "&:hover": {
                  backgroundColor: isSelected
                    ? "primary.main"
                    : "surface.inset",
                },
              }}
            >
              <Typography
                variant="L2"
                sx={{
                  color: isSelected
                    ? "primary.contrastText"
                    : day.inMonth
                      ? "text.primary"
                      : "text.muted",
                  // Padding days stay legible but visibly secondary.
                  opacity: day.inMonth ? 1 : 0.5,
                }}
              >
                {day.date.getDate()}
              </Typography>

              {/* The dot is decoration; `data-has-events` and the day list carry the
                  same information for assistive technology. */}
              <Box
                aria-hidden="true"
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  backgroundColor: hasEvents
                    ? isSelected
                      ? "primary.contrastText"
                      : "link.main"
                    : "transparent",
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
